import { useEffect, useRef, useState } from "react";

type Props = {
  url: string | null;
  volume: number;
  autoPlay?: boolean;
  onPlayingChange?: (playing: boolean) => void;
};

/** Chromium rejects play() when a later pause()/load() wins the race — not a user-facing failure. */
function isBenignPlayInterruption(err: unknown): boolean {
  if (!(err instanceof DOMException) && !(err instanceof Error)) return false;
  const name = "name" in err ? err.name : "";
  const message = err.message;
  return (
    name === "AbortError" ||
    message.includes("interrupted by a call to pause()") ||
    message.includes("interrupted by a new load request")
  );
}

export function PreviewPlayer({
  url,
  volume,
  autoPlay = true,
  onPlayingChange,
}: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const onPlayingChangeRef = useRef(onPlayingChange);
  const suppressPauseNotifyRef = useRef(false);
  const playGenRef = useRef(0);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onPlayingChangeRef.current = onPlayingChange;
  }, [onPlayingChange]);

  function setPlayingState(next: boolean) {
    setPlaying(next);
    onPlayingChangeRef.current?.(next);
  }

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.volume = Math.max(0, Math.min(1, volume));
  }, [volume]);

  useEffect(() => {
    const el = audioRef.current;
    const gen = ++playGenRef.current;

    if (!el || !url) {
      setPlayingState(false);
      return;
    }

    setError(null);
    suppressPauseNotifyRef.current = true;
    el.pause();
    el.currentTime = 0;
    // src is bound via React; load() picks up the current source without racing a second assignment.
    el.load();
    suppressPauseNotifyRef.current = false;

    if (!autoPlay) {
      setPlayingState(false);
      return () => {
        playGenRef.current += 1;
      };
    }

    void el
      .play()
      .then(() => {
        if (gen !== playGenRef.current) return;
        setPlayingState(true);
      })
      .catch((err) => {
        if (gen !== playGenRef.current) return;
        setPlayingState(false);
        if (isBenignPlayInterruption(err)) return;
        setError(err instanceof Error ? err.message : "Playback blocked");
      });

    return () => {
      // Invalidate in-flight play(); the next effect (or unmount) owns the element.
      playGenRef.current += 1;
      suppressPauseNotifyRef.current = true;
      el.pause();
      suppressPauseNotifyRef.current = false;
    };
  }, [url, autoPlay]);

  if (!url) return null;

  return (
    <div className="preview-player">
      <audio
        ref={audioRef}
        src={url}
        preload="auto"
        onPlay={() => setPlayingState(true)}
        onPause={() => {
          if (suppressPauseNotifyRef.current) return;
          setPlayingState(false);
        }}
        onEnded={() => setPlayingState(false)}
        onError={() => {
          setPlayingState(false);
          setError("Could not load preview audio");
        }}
      />
      <button
        type="button"
        className="btn"
        onClick={() => {
          const el = audioRef.current;
          if (!el) return;
          if (el.paused) {
            const gen = ++playGenRef.current;
            setError(null);
            void el
              .play()
              .then(() => {
                if (gen !== playGenRef.current) return;
                setPlayingState(true);
              })
              .catch((err) => {
                if (gen !== playGenRef.current) return;
                setPlayingState(false);
                if (isBenignPlayInterruption(err)) return;
                setError(err instanceof Error ? err.message : "Playback blocked");
              });
          } else {
            playGenRef.current += 1;
            el.pause();
            setPlayingState(false);
          }
        }}
      >
        {playing ? "Stop" : "Play preview"}
      </button>
      {error ? (
        <span className="readout" style={{ color: "var(--warn, var(--danger))" }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}
